import { getDictAll } from "@/api/system";
import { setStore, getStore, removeStore } from "./util";

const DICT_CACHE_KEY = "dictMap";

/**
 * 仅当明确停用时过滤（与门户端一致）
 * - status 为 undefined / null / "" → 视为启用
 * - status 为 "0" / 0 → 启用
 * - status 为 "1" / 1 → 停用（过滤）
 */
function isDictEnabled(item) {
  if (!item) return false;
  const status =
    item.status !== undefined && item.status !== null
      ? item.status
      : item.enableStatus !== undefined && item.enableStatus !== null
        ? item.enableStatus
        : item.isEnabled;
  if (status === undefined || status === null || status === "") {
    return true;
  }
  return String(status) === "0" || String(status) === "true";
}

function getItemDictType(item) {
  if (!item) return "";
  return item.dictType || item.dict_type || item.type || "";
}

function extractDictRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.list)) return payload.list;
  return [];
}

function normalizeDictItem(item = {}, fallbackType = "") {
  const dictType = getItemDictType(item) || fallbackType;
  return {
    ...item,
    dictType,
    dictLabel: item.dictLabel || item.dict_label || "",
    dictValue: item.dictValue || item.dict_value || "",
    dictSort:
      item.dictSort !== undefined && item.dictSort !== null
        ? item.dictSort
        : item.dict_sort !== undefined && item.dict_sort !== null
          ? item.dict_sort
          : 0,
    dictCode:
      item.dictCode !== undefined && item.dictCode !== null
        ? item.dictCode
        : item.dict_code !== undefined && item.dict_code !== null
          ? item.dict_code
          : ""
  };
}

function buildDictMap(rows = []) {
  const map = {};
  rows.forEach(item => {
    const dictType = getItemDictType(item);
    if (!dictType || !isDictEnabled(item)) {
      return;
    }
    if (!map[dictType]) {
      map[dictType] = [];
    }
    map[dictType].push(normalizeDictItem(item, dictType));
  });
  Object.keys(map).forEach(dictType => {
    map[dictType].sort(
      (a, b) => Number(a.dictSort || 0) - Number(b.dictSort || 0)
    );
  });
  return map;
}

function buildDictMapFromObject(data = {}) {
  const map = {};
  Object.keys(data).forEach(dictType => {
    const items = data[dictType];
    if (!Array.isArray(items)) {
      return;
    }
    map[dictType] = items
      .filter(item => isDictEnabled(item))
      .map(item => normalizeDictItem(item, dictType))
      .sort((a, b) => Number(a.dictSort || 0) - Number(b.dictSort || 0));
  });
  return map;
}

/**
 * 解析 dictAll 响应为 dictMap（与门户端一致）
 * axios 拦截器已解包 data，此处兼容未解包场景
 */
function parseDictResponse(res) {
  let payload = res;
  if (payload && typeof payload === "object" && "data" in payload) {
    payload = payload.data;
  }
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    payload.data &&
    (Array.isArray(payload.data) ||
      Array.isArray(payload.data.rows) ||
      Array.isArray(payload.data.list) ||
      (typeof payload.data === "object" &&
        Object.keys(payload.data).some(k => Array.isArray(payload.data[k]))))
  ) {
    payload = payload.data;
  }

  const rows = extractDictRows(payload);
  if (rows.length > 0 && rows.some(item => getItemDictType(item))) {
    return buildDictMap(rows);
  }

  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const typeKeys = Object.keys(payload).filter(
      key => Array.isArray(payload[key]) && !["rows", "list"].includes(key)
    );
    if (typeKeys.length) {
      const grouped = {};
      typeKeys.forEach(key => {
        grouped[key] = payload[key];
      });
      return buildDictMapFromObject(grouped);
    }
  }

  return {};
}

/** 获取本地字典 Map */
export function getDictMap() {
  const raw = getStore(DICT_CACHE_KEY);
  if (!raw) {
    return {};
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
}

/** 拉取全量字典并写入 localStorage（门户 dictAll） */
export async function loadDictCache() {
  const res = await getDictAll();
  const map = parseDictResponse(res);
  setStore(DICT_CACHE_KEY, map);
  return map;
}

/** 本地无缓存时拉取字典 */
export async function ensureDictCache(requiredType) {
  const map = getDictMap();
  if (Object.keys(map).length > 0) {
    if (!requiredType || (map[requiredType] && map[requiredType].length)) {
      return map;
    }
  }
  return loadDictCache();
}

/** 清除字典缓存 */
export function clearDictCache() {
  removeStore(DICT_CACHE_KEY);
}

/** 获取某字典类型的选项列表 */
export function getDictOptions(dictType) {
  const map = getDictMap();
  return map[dictType] ? map[dictType].slice() : [];
}

/** 获取某字典类型选项，dictValue 转为数字（适用于下拉框） */
export function getIntDictOptions(dictType) {
  return getDictOptions(dictType).map(item => ({
    ...item,
    dictValue: isNaN(Number(item.dictValue))
      ? item.dictValue
      : Number(item.dictValue)
  }));
}

/** 根据 dictType + dictValue 获取展示文案（支持 CSV / 数组） */
export function getDictLabel(dictType, value, separator = ",") {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const values = Array.isArray(value)
    ? value.map(String)
    : String(value)
        .split(separator)
        .map(item => item.trim())
        .filter(Boolean);
  if (!values.length) return "";
  return values
    .map(v => {
      const item = getDictOptions(dictType).find(
        d => String(d.dictValue) === String(v)
      );
      return item ? item.dictLabel : String(v);
    })
    .join("、");
}

/** 根据 dictType + dictValue 获取字典项 */
export function getDictItem(dictType, value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return (
    getDictOptions(dictType).find(d => String(d.dictValue) === String(value)) ||
    null
  );
}

/**
 * 字典类型常量（芋道 DICT_TYPE 风格）
 * 与后端 sys_dict.dict_type 对齐（课程模块）
 * - 课程分类 course_type
 * - 职业路径 learning_path_type
 * - 学习路径 learn_system_type
 * - 推荐分类 recommend_type
 */
export const DICT_TYPE = {
  COURSE_TYPE: "course_type",
  COURSE_STATUS: "course_status",
  COURSE_LEVEL: "course_level",
  LEARNING_PATH: "learning_path_type",
  LEARNING_SYSTEM: "learn_system_type",
  RECOMMEND_TYPE: "recommend_type",
  RESOURCE_TYPE: "resource_type",
  SIGNUP_AUDIT_STATUS: "signup_audit_status",
  THEORY_TYPE: "theory_type",
  PRACTICAL_TYPE: "practical_type",
  TERM: "term"
};

export const practicalParams = {
  1: {
    label: "图像分类",
    value: 1,
    params: {
      type: "file",
      fileTypes: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
      accept: "image/*",
      maxSize: 10,
      title:
        "识别图片中的物体或场景，分配正确类别标签。评分依据为分类的准确性。"
    }
  },
  2: {
    label: "图像标注",
    value: 2,
    params: {
      type: "file",
      fileTypes: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
      accept: "image/*",
      maxSize: 10,
      title:
        "根据给定图像，考生需使用边界框标注物体，并正确分配类别标签。评分基于标注的准确性和完整性。"
    }
  },
  7: {
    label: "图像分割",
    value: 7,
    params: {
      type: "file",
      fileTypes: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
      accept: "image/*",
      maxSize: 10,
      title:
        "对图像每个像素进行语义分割并标注类别，评分基于分割区域的准确性及物体边缘的清晰度。"
    }
  },
  3: {
    label: "视频识别",
    value: 3,
    params: {
      type: "file",
      accept: "video/*",
      maxSize: 50, // MB
      fileTypes: ["mp4", "avi", "mov", "mkv"],
      title:
        "识别视频中的主要活动或动作，分配正确类别标签。评分依据为动作识别的准确性及视频理解。"
    }
  },
  4: {
    label: "音频识别",
    value: 4,
    params: {
      type: "file",
      accept: "audio/*",
      maxSize: 10, // MB
      fileTypes: ["mp3", "wav", "ogg", "aac"],
      title:
        "根据音频特征进行分类并标注类别，评分标准为音频特征捕捉和分类准确性。"
    }
  },
  5: {
    label: "时序检测",
    value: 5,
    params: {
      type: "text",
      title:
        "考生需标注出与指标属性不匹配的异常点，评分标准为标注的准确性与合理性。"
    }
  },
  6: { label: "简答题", value: 6, params: { type: "text" } }
};
export const examLevel = [
  { label: "初级", value: 1 },
  { label: "中级", value: 2 },
  { label: "高级", value: 3 }
];
export const getExamLabel = val => {
  if (!val || ![1, 2, 3].includes(Number(val))) return "";
  return val ? examLevel.find(item => item.value === val).label : "";
};



// WEBPACK FOOTER //
// ./src/utils/dict.js